import { Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAutoOpenDelay from 'app/hooks/useAutoOpenDelay';
import useOpenOnScroll from 'app/hooks/useOpenOnScroll';
import { cx } from '@twind/core';
import { SecondaryIndicationSelector } from 'app/components/IndicationSelector';
import useHeader from 'app/hooks/useHeader';
import { useTailorHalfOpen } from 'app/hooks/useTailorHalfOpen';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';
import { ChatVersion } from 'app/copied/constants/common';
import useUnityMode, { UnityMode } from 'app/hooks/useUnityMode';
import useVirtualAssistantTracking from 'app/modules/virtual-assistant/hooks/useVirtualAssistantTracking';
import { stripHTML } from 'app/utils/stripHTML';
import useUiConfig from 'app/hooks/useUiConfig';
import useIsIntegratedView from 'app/hooks/useIsIntegratedView';

import useAutoOpenEnabled from '../../../../hooks/useAutoOpenEnabled';
import Header from '../../../../components/Header';
import TailorMostRequested from '../../components/TailorMostRequested';
import TailorFooter from '../../components/TailorFooter';
import TailorPrivacyBanner from '../../components/TailorPrivacyBanner';
import useFetchHelpCenter from '../../../../hooks/fetch/useFetchHelpCenter';
import useCategories from '../../../../hooks/useCategories';
import useHorizontalButtons from '../../../../hooks/useHorizontalButtons';
import { useLanguages } from '../../../../hooks/useOstroModules';
import TailorHorizontalButtons from '../../components/TailorHorizontalButtons';
import { Title } from '../../../../components/Typography';
import TailorLanguageSelector from '../../components/TailorLanguageSelector';
import { updateIDs } from '../../../../copied/getIds';
import useModule from '../../../../hooks/useModule';
import { ChatModuleType, SuggestedEntry } from '../../../../copied/models/ChatModules';
import useNamespacedStorage from '../../../../hooks/storage/useNamespacedStorage';
import useHandleSearchResultClick from '../../hooks/useHandleSearchResultClick';
import { ModuleItem } from '../../../../services/helpCenter';
import queryClient from '../../../../services/queryClient';
import useTailorTracking from '../../hooks/useTailorTracking';
import TailorLiveChatModule from '../../components/TailorLiveChatModule';
import CustomScrollbar from '../../../../components/CustomScrollbar';
import IntegratedSearchInput from '../../components/IntegratedSearchInput';
import TailorSearchButton from '../../components/TailorSearchButton';
import { useSearch } from '../../hooks/useSearch';
import useWebchatVersion from '../../../../hooks/useWebchatVersion';
import TailorLandingPills from './TailorLandingPills';
import TailorLandingCategories from './TailorLandingCategories';
export default function Tailor() {
  const { t } = useTranslation();
  const webchatVersion = useWebchatVersion();
  const { isOpen } = useTailorIsOpen();
  const { halfOpen } = useTailorHalfOpen();
  const navigate = useNavigate();
  const { showLanguageSelector, webchatLanguageModules, selectedLanguageCondition } = useLanguages();
  const { data: helpCenter } = useFetchHelpCenter();
  const {
    captureLandingPageSearchInputClicked,
    captureLandingPageCTAClicked,
    captureLandingPagePageview,
    captureLanguageSwitch,
    captureLandingPageVAClicked,
    captureHalfOpen,
  } = useTailorTracking();
  const { captureQuerySubmitted } = useVirtualAssistantTracking();
  const { setSearch } = useSearch();
  const uiConfig = useUiConfig();
  const isIntegratedView = useIsIntegratedView();
  const isAutoOpenEnabled = useAutoOpenEnabled();
  const autoOpenDelay = useAutoOpenDelay();
  const categories = useCategories();
  const horizontalButtons = useHorizontalButtons();
  const handleResultSelect = useHandleSearchResultClick();
  const tailorModule = useModule(ChatModuleType.TAIlor);
  const vaModule = useModule(ChatModuleType.VirtualAssistant);
  const liveChatModule = useModule(ChatModuleType.LiveChat);
  const integratedSearchModule = useModule(ChatModuleType.IntegratedSearch);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useOpenOnScroll(scrollContainerRef);
  const { headerIcon, headerLines, headerTextEnabled } = useHeader();
  const unityMode = useUnityMode();
  const isV41 = webchatVersion === ChatVersion.v4_1;

  const [searchInputValue, setSearchInputValue] = useState('');

  const [, setLanguageCondition] = useNamespacedStorage('ostroLanguageCondition');

  const onClickSearch = (search?: string) => {
    if (!integratedSearchModule || !search) {
      setSearch('');
    } else {
      setSearch(search);
    }

    captureLandingPageSearchInputClicked();

    const isSearchOnly = unityMode === UnityMode.SEARCH_ONLY;
    const sanitizedSearch = stripHTML(search);

    if (isV41 && !isSearchOnly) {
      captureQuerySubmitted({
        query: sanitizedSearch || '',
        isConvAiActive: !!vaModule?.va_convo_ai_enabled,
      });
      return navigate(`/virtual-assistant/search/${sanitizedSearch}`);
    }

    return navigate('/tailor/search', {
      state: {
        prevPath: '/tailor',
        nextPath: '/tailor/search',
      },
    });
  };

  const onClickSuggested = (result: SuggestedEntry, index: number) => {
    handleResultSelect({
      result,
      resultIndex: index + 1,
      isSuggested: true,
    });
  };

  const onClickMostRequestedResult = (result: SuggestedEntry, index: number) => {
    handleResultSelect({
      result,
      resultIndex: index + 1,
      isSuggested: true,
      linkLocation: 'topicPinnedResource',
    });
  };

  const onClickHorizontalButton = (button: ModuleItem) => {
    captureLandingPageCTAClicked(button);
  };

  useEffect(() => {
    if (!isOpen) return;
    captureLandingPagePageview({
      autoOpen: isAutoOpenEnabled,
      autoopenDelay: autoOpenDelay,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (halfOpen) {
      captureHalfOpen(halfOpen, autoOpenDelay);
    }

    if (integratedSearchModule) {
      setSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!helpCenter) return null;

  return (
    <div className="flex flex-col h-full">
      <CustomScrollbar
        id="ostro-tailor-landing-container"
        rememberScrollPosition
        containerRef={scrollContainerRef}
        className="overflow-y-auto flex grow flex-col relative"
      >
        <div
          className={cx(
            '~(bg-brand-panelTop rounded-b-2xl px-7 pt-5)',
            webchatVersion === ChatVersion.v4_1 ? 'px-4' : ''
          )}
        >
          <Header
            showBackButton={false}
            showExpandButton={halfOpen}
            headerIcon={headerIcon}
            headerTitle={headerLines?.[0]}
            headerTextEnabled={headerTextEnabled}
            className={`${integratedSearchModule ? 'mb-6' : ''}`}
            useCustomIndicationSelectorButton={isV41}
          />
          {integratedSearchModule ? (
            <IntegratedSearchInput
              placeholder={integratedSearchModule.search_suggestions?.[0]?.text?.replace(/<[^>]*>/g, '')}
              value={searchInputValue}
              onChange={setSearchInputValue}
              onClickSearch={(search: string) => onClickSearch(search)}
              disableWhenEmpty={unityMode === UnityMode.SEARCH_ONLY}
              ariaLabel={t('aria_labels.landing_search')}
            />
          ) : (
            <TailorSearchButton onClick={onClickSearch} />
          )}
          <TailorLandingPills />
        </div>
        <TailorLandingCategories
          categories={categories}
          onClickFaq={onClickSuggested}
          className={isV41 ? 'pb-3' : ''}
        />
        {(horizontalButtons || categories.length > 0 || (isV41 && liveChatModule)) && (
          <div
            id="ostro-tailor-landing-bottom-container"
            className="rounded-t-custom bg-brand-panelBottom py-6 px-8"
          >
            {horizontalButtons && (
              <TailorHorizontalButtons
                title={''}
                buttons={horizontalButtons?.items}
                onClickButton={onClickHorizontalButton}
                className="mb-8"
              />
            )}
            {liveChatModule && <TailorLiveChatModule className="mb-8" />}
            {!vaModule && categories.length > 0 && (
              <TailorMostRequested
                onSelectResult={onClickMostRequestedResult}
                searchResults={tailorModule?.config?.suggested_entries}
              />
            )}
            {!isV41 && vaModule?.items?.length && (
              <TailorMostRequested
                id="ostro-tailor-va-landing-container"
                title={vaModule?.header_title}
                showArticleDescriptions={false}
                articleTitleComponent={Title}
                searchResults={vaModule?.items?.map((item) => ({
                  id: '',
                  title: item.title,
                  description: '',
                  render_json: [],
                  external_link: '',
                  resource_link: '',
                }))}
                onSelectResult={(result) => {
                  captureLandingPageVAClicked({
                    topicType: 'VirtualChatTopic',
                    topicTitle: result.title,
                  });
                  navigate(
                    `/virtual-assistant/${vaModule?.items?.find((item) => item.title === result.title)?.action}`
                  );
                }}
              />
            )}
          </div>
        )}
        <SecondaryIndicationSelector className="px-7 py-5 grow" />
      </CustomScrollbar>
      <Outlet />
      <div className="shrink-0">
        {isIntegratedView ? (
          uiConfig?.integratedView?.showFooter ? (
            <TailorFooter />
          ) : null
        ) : !halfOpen ? (
          <TailorFooter />
        ) : null}
        <TailorPrivacyBanner footerText={helpCenter.footer.lines} />
        {showLanguageSelector && (
          <TailorLanguageSelector
            languages={webchatLanguageModules}
            onLanguageChange={async (langCondition, langCode, prevLangCode) => {
              setLanguageCondition(langCondition);
              captureLanguageSwitch(langCondition, langCode, prevLangCode);
              await updateIDs();
              await queryClient.invalidateQueries();
            }}
            selectedLanguageCondition={selectedLanguageCondition}
          />
        )}
      </div>
    </div>
  );
}
